import React from 'react';
import { useMediaQuery } from 'react-responsive';

import {
  Card,
  CardBody,
  CardDivider,
  CardHeader,
  Cards,
  CheckIcon,
  Connector,
  ConnectorFill,
  Container,
  CurrentDot,
  IconStep,
  Journey,
  NameStep,
  Step,
  StepLabel,
  StepName,
  StepRow,
  StepStatusText,
  StepTexts,
} from './styles';
import { StepperStep, StepStatus } from './types';

export type { StepperStep, StepStatus };

export type StepperProps = {
  steps: StepperStep[];
  // eslint-disable-next-line react/require-default-props
  children?: React.ReactNode;
  // eslint-disable-next-line react/require-default-props
  className?: string;
};

const MOBILE_BREAKPOINT = 768;

const STATUS_TEXT: Record<StepStatus, string> = {
  pendente: 'Pendente',
  'em-processo': 'Em processo',
  concluida: 'Concluída',
};

export const connectorRatio = (step: StepperStep): number => {
  if (step.status === 'concluida') return 1;

  return Math.min(Math.max(step.progress ?? 0, 0), 1);
};

export const activeStepIndex = (steps: StepperStep[]): number => {
  const emProcesso = steps.findIndex((step) => step.status === 'em-processo');

  return emProcesso === -1 ? steps.length - 1 : emProcesso;
};

const Check = (): JSX.Element => (
  <CheckIcon viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 6.25L4.75 8.5L9.5 3.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </CheckIcon>
);

const StepIcon = ({ status }: { status: StepStatus }): JSX.Element => (
  <IconStep status={status}>
    {status === 'em-processo' && <CurrentDot />}
    {status === 'concluida' && <Check />}
  </IconStep>
);

const Stepper = ({ steps, children, className }: StepperProps): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: MOBILE_BREAKPOINT });

  if (isMobile) {
    const ativo = activeStepIndex(steps);

    return (
      <Cards
        className={className}
        role="list"
        aria-label="Progresso do cadastro"
      >
        {steps.map((step, index) => (
          <Card key={step.label} role="listitem">
            <CardHeader>
              <StepRow>
                <StepIcon status={step.status} />
                <StepTexts>
                  <NameStep>
                    <StepLabel>{step.label}</StepLabel>
                    <StepName>{step.name}</StepName>
                  </NameStep>
                  <StepStatusText status={step.status}>
                    {STATUS_TEXT[step.status]}
                  </StepStatusText>
                </StepTexts>
              </StepRow>
            </CardHeader>

            {index === ativo && !!children && (
              <>
                <CardDivider />
                <CardBody>{children}</CardBody>
              </>
            )}
          </Card>
        ))}
      </Cards>
    );
  }

  return (
    <Journey className={className}>
      <Container role="list" aria-label="Progresso do cadastro">
        {steps.slice(0, -1).map((step, index) => (
          <Connector key={`conector-${step.label}`} index={index}>
            <ConnectorFill
              ratio={connectorRatio(step)}
              done={step.status === 'concluida'}
            />
          </Connector>
        ))}

        {steps.map((step, index) => (
          <Step key={step.label} index={index} role="listitem">
            <StepIcon status={step.status} />
            <NameStep>
              <StepLabel>{step.label}</StepLabel>
              <StepName>{step.name}</StepName>
            </NameStep>
            <StepStatusText status={step.status}>
              {STATUS_TEXT[step.status]}
            </StepStatusText>
          </Step>
        ))}
      </Container>

      {children}
    </Journey>
  );
};

export default Stepper;
